import type { AttendeeFreeBusy } from '@renderer/components/action-tags/day-timeline-layout'

/**
 * Align a free/busy response to the REQUESTED emails: exactly one row per unique
 * requested email, using the provider's entry when present (matched
 * case-insensitively) and synthesizing `unknown` for any the provider omitted or
 * a connection that couldn't be queried at all.
 *
 * This guarantees every invited guest appears (busy / free / unknown), so the
 * overlay's `hasAttendees` / footer / `reconnectRequired` state reflects the
 * INVITED guests rather than the completeness of the response — a 200 that comes
 * back with fewer rows (or, defensively, none) can't silently hide the
 * availability section or the reconnect prompt. Mirrors the backend's documented
 * "absent → unknown" contract and its missing-scope `allUnknown` behaviour.
 */
export function alignFreeBusyAttendees(
  emails: Array<string>,
  responseAttendees: Array<AttendeeFreeBusy>,
): Array<AttendeeFreeBusy> {
  const byEmail = new Map(responseAttendees.map((a) => [a.email.trim().toLowerCase(), a]))
  const seen = new Set<string>()
  const aligned: Array<AttendeeFreeBusy> = []
  for (const email of emails) {
    const key = email.trim().toLowerCase()
    if (!key || seen.has(key)) continue
    seen.add(key)
    aligned.push(byEmail.get(key) ?? { email, status: 'unknown', busy: [] })
  }
  return aligned
}
